import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  FaFire,
  FaMousePointer,
  FaArrowsAltH,
  FaClock,
  FaChartBar,
  FaExclamationCircle,
} from 'react-icons/fa';
import {
  fetchBehaviorMetrics,
  calculateBehaviorStats,
  calculateTypeSpecificStats,
  formatBehaviorType,
  getBehaviorIcon,
  transformBehaviorResponse,
} from '../services/behaviorMetricsService';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Auto-refresh interval in milliseconds */
const REFRESH_INTERVAL_MS = 60_000;

/** Maps period day counts to dateRange string values */
const DAYS_TO_DATE_RANGE = { 7: '7days', 30: '30days', 90: '90days' };

/**
 * Static configuration for each behavior type.
 * Used to drive colors, labels, icons, and filter buttons consistently.
 */
const BEHAVIOR_CONFIG = {
  rage_click: {
    label: 'Rage Clicks',
    emoji: '😡',
    icon: FaFire,
    color: 'text-red-400',
    activeClass: 'bg-red-500 text-white',
    badgeClass: 'bg-red-900 text-red-200',
    barClass: 'bg-red-500',
  },
  dead_click: {
    label: 'Dead Clicks',
    emoji: '💀',
    icon: FaMousePointer,
    color: 'text-gray-400',
    activeClass: 'bg-gray-600 text-white',
    badgeClass: 'bg-gray-700 text-gray-200',
    barClass: 'bg-gray-500',
  },
  u_turn: {
    label: 'U-Turns',
    emoji: '📍',
    icon: FaArrowsAltH,
    color: 'text-purple-400',
    activeClass: 'bg-purple-500 text-white',
    badgeClass: 'bg-purple-900 text-purple-200',
    barClass: 'bg-purple-500',
  },
  quick_back: {
    label: 'Quick Backs',
    emoji: '⏪',
    icon: FaClock,
    color: 'text-yellow-400',
    activeClass: 'bg-yellow-500 text-white',
    badgeClass: 'bg-yellow-900 text-yellow-200',
    barClass: 'bg-yellow-500',
  },
};

// ─── Sub-components ────────────────────────────────────────────────────────────

/**
 * Displays a spinning loader centered on the page.
 */
const LoadingState = () => (
  <div className="flex-1 flex items-center justify-center bg-[#1E1F22] min-h-screen">
    <div className="flex items-center gap-3">
      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
      <span className="text-blue-300">Loading behavior metrics...</span>
    </div>
  </div>
);

/**
 * Summary card for a single metric category.
 *
 * @param {string}   title - Card label
 * @param {number}   value - Numeric value to display
 * @param {React.FC} icon  - React icon component
 * @param {string}   color - Tailwind text-color class for value and icon
 */
const StatCard = ({ title, value, icon: Icon, color = 'text-blue-400' }) => (
  <div className="bg-[#1D1E22] rounded-lg p-4 border border-gray-800">
    <div className="flex justify-between items-start">
      <div>
        <span className="text-gray-400 text-sm mb-2 block">{title}</span>
        <span className={`text-3xl font-bold ${color}`}>{value}</span>
      </div>
      <Icon className={`w-8 h-8 ${color}`} />
    </div>
  </div>
);

/**
 * Horizontal progress bar for ranking list items.
 *
 * @param {number} value    - Current item value
 * @param {number} maxValue - Highest value in the list (denominator for percentage)
 * @param {string} barClass - Tailwind bg-color class for the filled bar
 */
const RankBar = ({ value, maxValue, barClass }) => (
  <div className="w-full bg-[#2D2F33] rounded-full h-2">
    <div
      className={`${barClass} h-full rounded-full`}
      style={{ width: `${(value / Math.max(maxValue, 1)) * 100}%` }}
    />
  </div>
);

/**
 * A single ranked row showing an element/page label, count badge, and bar.
 *
 * @param {string} label      - Text label (element selector or page path)
 * @param {number} count      - Occurrence count
 * @param {number} maxCount   - Max count in the list for bar scaling
 * @param {string} badgeClass - Tailwind classes for the count badge
 * @param {string} barClass   - Tailwind class for the bar fill color
 */
const RankedItem = ({ label, count, maxCount, badgeClass, barClass }) => (
  <div className="bg-[#1D1E22] p-3 rounded-lg">
    <div className="flex justify-between items-start mb-2">
      <span className="text-gray-300 text-sm truncate font-mono">{label}</span>
      <span className={`text-xs px-2 py-1 rounded ${badgeClass}`}>{count}</span>
    </div>
    <RankBar value={count} maxValue={maxCount} barClass={barClass} />
  </div>
);

/**
 * Ranked list panel with a title and icon.
 *
 * @param {string}   title     - Section heading
 * @param {React.FC} icon      - Icon component shown in the heading
 * @param {string}   iconClass - Tailwind color class for the icon
 * @param {Array}    items     - Array of { label, count } objects
 * @param {string}   badgeClass
 * @param {string}   barClass
 * @param {string}   emptyMsg  - Fallback message when items is empty
 */
const RankedListPanel = ({ title, icon: Icon, iconClass, items, badgeClass, barClass, emptyMsg }) => {
  const maxCount = items[0]?.count ?? 1;

  return (
    <div className="lg:col-span-1 bg-[#2D2F33] rounded-lg p-6 border border-gray-800">
      <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
        <Icon className={iconClass} />
        {title}
      </h3>

      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <RankedItem
              key={idx}
              label={item.label}
              count={item.count}
              maxCount={maxCount}
              badgeClass={badgeClass}
              barClass={barClass}
            />
          ))}
        </div>
      ) : (
        <p className="text-gray-400 text-sm">{emptyMsg}</p>
      )}
    </div>
  );
};

/**
 * Filter pill buttons for selecting a specific behavior type.
 *
 * @param {string|null} selected   - Currently active behavior type key, or null for "All"
 * @param {Function}    onSelect   - Callback when a filter button is clicked
 */
const BehaviorTypeFilter = ({ selected, onSelect }) => (
  <div className="flex gap-2 flex-wrap">
    {/* "All" pill */}
    <button
      onClick={() => onSelect(null)}
      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        selected === null ? 'bg-blue-500 text-white' : 'bg-[#2D2F33] text-gray-300 hover:bg-[#3A3C40]'
      }`}
    >
      All
    </button>

    {/* One pill per behavior type */}
    {Object.entries(BEHAVIOR_CONFIG).map(([key, cfg]) => (
      <button
        key={key}
        onClick={() => onSelect(key)}
        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          selected === key ? cfg.activeClass : 'bg-[#2D2F33] text-gray-300 hover:bg-[#3A3C40]'
        }`}
      >
        {cfg.emoji} {cfg.label}
      </button>
    ))}
  </div>
);

/**
 * Error banner with a retry button.
 *
 * @param {string}   message  - Error message to display
 * @param {Function} onRetry  - Called when the user clicks Retry
 */
const ErrorBanner = ({ message, onRetry }) => (
  <div className="mb-6 p-4 bg-red-900/20 border border-red-700/50 rounded-lg flex items-start gap-3">
    <FaExclamationCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
    <div className="flex-1">
      <p className="text-red-300 font-semibold">Error Loading Behavior Metrics</p>
      <p className="text-red-200 text-sm mt-1">{message}</p>
    </div>
    <button
      onClick={onRetry}
      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap flex-shrink-0"
    >
      Retry
    </button>
  </div>
);

/**
 * Formats a single event row's "Details" cell based on behavior type.
 *
 * @param {Object} metric - A single behavior metric record
 * @returns {string}
 */
const formatEventDetails = (metric) => {
  switch (metric.behaviorType) {
    case 'rage_click':
      return `${metric.clickCount} clicks in ${metric.timeSpan}ms`;
    case 'dead_click':
      return metric.details?.elementId ?? 'unknown element';
    default:
      return metric.details?.intermediaryPage ?? 'back and forth';
  }
};

// ─── Main Component ────────────────────────────────────────────────────────────

/**
 * BehaviorMetricsDashboard
 *
 * Displays rage clicks, dead clicks, U-turns, and quick backs analytics.
 * Fetches data on mount and refreshes every 60 seconds.
 *
 * @param {string} projectTitle - Label shown in the header badge (default: "All Experiences")
 */
const BehaviorMetricsDashboard = ({ projectTitle = 'All Experiences' }) => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [metricsData, setMetricsData]               = useState([]);
  const [stats, setStats]                           = useState(null);
  const [loading, setLoading]                       = useState(false);
  const [error, setError]                           = useState(null);
  const [selectedBehaviorType, setSelectedBehaviorType] = useState(null);
  const [period, setPeriod]                         = useState(null);

  // ── Data Fetching ──────────────────────────────────────────────────────────

  /**
   * Loads behavior metrics from the API, transforms the response,
   * and updates all relevant state slices.
   */
  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchBehaviorMetrics({});

      if (!data) return;

      // Store period metadata and sync the dateRange selector
      if (data.period) {
        setPeriod(data.period);
        console.log('📅 Period Data:', data.period);
      }

      // Flatten the API response into a uniform metrics array
      const transformed = transformBehaviorResponse(data);
      console.log('✅ Transformed Metrics:', {
        total: transformed.length,
        byType: Object.fromEntries(
          Object.keys(BEHAVIOR_CONFIG).map((type) => [
            type,
            transformed.filter((m) => m.behaviorType === type).length,
          ])
        ),
      });

      setMetricsData(transformed);
      const calculated = calculateBehaviorStats(transformed);
      setStats(calculated);
      console.log('✓ Behavior Stats:', calculated);
    } catch (err) {
      console.error('Error fetching behavior metrics:', err);
      setError(err.message ?? 'Failed to fetch behavior metrics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and refresh on a fixed interval
  useEffect(() => {
    fetchMetrics();
    const intervalId = setInterval(fetchMetrics, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchMetrics]);

  // ── Derived Data ───────────────────────────────────────────────────────────

  /**
   * Metrics filtered by the currently selected behavior type (or all if null).
   */
  const filteredMetrics = useMemo(
    () =>
      selectedBehaviorType
        ? metricsData.filter((m) => m.behaviorType === selectedBehaviorType)
        : metricsData,
    [metricsData, selectedBehaviorType]
  );

  /**
   * Stats recalculated whenever the filter or underlying data changes.
   * When a specific type is selected we use type-specific stats; otherwise
   * we use the overall summary stats.
   */
  const filteredStats = useMemo(() => {
    if (!selectedBehaviorType) return calculateBehaviorStats(filteredMetrics);

    const typeStats = calculateTypeSpecificStats(filteredMetrics, selectedBehaviorType);
    // Expose a unified `totalRageClicks` key for the summary card
    return { ...typeStats, totalRageClicks: filteredMetrics.length };
  }, [filteredMetrics, selectedBehaviorType]);

  /**
   * Normalises the top-elements list for the generic RankedListPanel.
   * The "all" view uses `rageClickCount`; type-specific views use `count`.
   */
  const topElementsForPanel = useMemo(() => {
    if (!filteredStats) return [];

    if (selectedBehaviorType) {
      return (filteredStats.topElements ?? []).map((item) => ({
        label: item.element,
        count: item.count,
      }));
    }

    return (filteredStats.topElementsWithRageClicks ?? []).map((item) => ({
      label: item.element,
      count: item.rageClickCount,
    }));
  }, [filteredStats, selectedBehaviorType]);

  /**
   * Normalises the top-pages list for the generic RankedListPanel.
   */
  const topPagesForPanel = useMemo(() => {
    if (!filteredStats) return [];

    if (selectedBehaviorType) {
      return (filteredStats.topPages ?? []).map((item) => ({
        label: item.page,
        count: item.count,
      }));
    }

    return (filteredStats.mostUTurnPages ?? []).map((item) => ({
      label: item.page,
      count: item.uTurnCount,
    }));
  }, [filteredStats, selectedBehaviorType]);

  // ── Early Returns ──────────────────────────────────────────────────────────

  if (loading && !error) return <LoadingState />;

  // ── Helpers for active behavior config ────────────────────────────────────

  /**
   * Returns the BEHAVIOR_CONFIG entry for the selected type,
   * or sensible defaults for the "All" view.
   */
  const activeCfg = selectedBehaviorType
    ? BEHAVIOR_CONFIG[selectedBehaviorType]
    : { icon: FaFire, color: 'text-red-400', badgeClass: 'bg-red-900 text-red-200', barClass: 'bg-red-500' };

  const uTurnCfg = BEHAVIOR_CONFIG.u_turn;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex-1 p-6 overflow-x-hidden bg-[#121316] max-h-screen">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Behavior Metrics</h1>
          <p className="text-gray-400 text-sm">Track user frustration and navigation patterns</p>
        </div>
        <div className="bg-[#2D2F33] rounded-lg px-4 py-2 flex items-center gap-2">
          <span className="text-gray-300 text-sm">Project:</span>
          <span className="font-medium text-white">{projectTitle}</span>
        </div>
      </div>

      {/* ── Data Period Banner ─────────────────────────────────────────────── */}
      {period && (
        <div className="mb-6 p-4 bg-[#2D2F33] rounded-lg border border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-gray-300 font-medium">Data Period:</span>
            <span className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold">
              Last {period.days} days
            </span>
          </div>
          <span className="text-xs text-gray-400">
            {new Date(period.from).toLocaleDateString()} → {new Date(period.to).toLocaleDateString()}
          </span>
        </div>
      )}

      {/* ── Error Banner ───────────────────────────────────────────────────── */}
      {error && (
        <ErrorBanner message={error} onRetry={() => window.location.reload()} />
      )}

      {/* ── Summary Stat Cards ─────────────────────────────────────────────── */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard title="Rage Clicks"  value={filteredStats.totalRageClicks}  icon={FaFire}         color="text-red-400"    />
          <StatCard title="Dead Clicks"  value={filteredStats.totalDeadClicks}  icon={FaMousePointer} color="text-gray-400"   />
          <StatCard title="U-Turns"      value={filteredStats.totalUTurns}      icon={FaArrowsAltH}   color="text-purple-400" />
          <StatCard title="Quick Backs"  value={filteredStats.totalQuickBacks}  icon={FaClock}        color="text-yellow-400" />
        </div>
      )}

      {/* ── Behavior Type Filter ───────────────────────────────────────────── */}
      <div className="mb-6">
        <label className="block text-gray-300 text-sm font-medium mb-3">
          Filter by behavior type:
        </label>
        <BehaviorTypeFilter
          selected={selectedBehaviorType}
          onSelect={setSelectedBehaviorType}
        />
      </div>

      {/* ── Main Content Grid ──────────────────────────────────────────────── */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left panel: Top Elements */}
        <RankedListPanel
          title={selectedBehaviorType ? 'Top Elements' : 'Top Rage Click Elements'}
          icon={activeCfg.icon}
          iconClass={activeCfg.color}
          items={topElementsForPanel}
          badgeClass={activeCfg.badgeClass}
          barClass={activeCfg.barClass}
          emptyMsg="No elements detected"
        />

        {/* Middle panel: Top Pages / U-Turn Pages */}
        <RankedListPanel
          title={selectedBehaviorType ? 'Top Pages' : 'Pages with Most U-Turns'}
          icon={selectedBehaviorType ? activeCfg.icon : uTurnCfg.icon}
          iconClass={selectedBehaviorType ? activeCfg.color : uTurnCfg.color}
          items={topPagesForPanel}
          badgeClass={selectedBehaviorType ? activeCfg.badgeClass : uTurnCfg.badgeClass}
          barClass={selectedBehaviorType ? activeCfg.barClass : uTurnCfg.barClass}
          emptyMsg={selectedBehaviorType ? 'No pages detected' : 'No U-turns detected'}
        />

        {/* Right panel: Summary stats */}
        <div className="lg:col-span-1 bg-[#2D2F33] rounded-lg p-6 border border-gray-800">
          <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
            <FaChartBar className="text-blue-400" />
            Summary
          </h3>
          <div className="space-y-4">
            {/* Average count */}
            <div className="bg-[#1D1E22] p-3 rounded-lg">
              <div className="text-gray-400 text-sm mb-1">
                {selectedBehaviorType
                  ? `Avg ${formatBehaviorType(selectedBehaviorType)} Count`
                  : 'Avg Rage Click Count'}
              </div>
              <div className="text-2xl font-bold text-blue-400">
                {selectedBehaviorType
                  ? filteredStats?.averageCount ?? 0
                  : filteredStats?.averageRageClickCount ?? 0}
              </div>
            </div>

            {/* Total events recorded */}
            <div className="bg-[#1D1E22] p-3 rounded-lg">
              <div className="text-gray-400 text-sm mb-1">Total Metrics Recorded</div>
              <div className="text-2xl font-bold text-green-400">{filteredMetrics.length}</div>
            </div>

            {/* Frustration index: ratio of rage clicks to total events */}
            <div className="bg-[#1D1E22] p-3 rounded-lg">
              <div className="text-gray-400 text-sm mb-1">Frustration Index</div>
              <div className="text-2xl font-bold text-orange-400">
                {(
                  ((filteredStats?.totalRageClicks ?? 0) /
                    Math.max(filteredMetrics.length, 1)) *
                  100
                ).toFixed(1)}
                %
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Recent Events Table ────────────────────────────────────────────── */}
      <div className="mt-8 bg-[#2D2F33] rounded-lg p-6 border border-gray-800">
        <h3 className="text-white font-bold text-lg mb-4">Recent Behavior Events</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 border-b border-gray-700">
                <th className="text-left py-3 px-4">Type</th>
                <th className="text-left py-3 px-4">Page</th>
                <th className="text-left py-3 px-4">Details</th>
                <th className="text-left py-3 px-4">Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredMetrics.slice(0, 10).map((metric, idx) => (
                <tr
                  key={idx}
                  className="border-b border-gray-700 hover:bg-[#1D1E22] transition-colors"
                >
                  <td className="py-3 px-4">
                    <span className="text-lg">{getBehaviorIcon(metric.behaviorType)}</span>
                    <span className="text-gray-300 ml-2">
                      {formatBehaviorType(metric.behaviorType)}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-gray-300 font-mono text-xs">{metric.page}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-gray-400 text-xs">{formatEventDetails(metric)}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-gray-400 text-xs">
                      {new Date(metric.timestamp).toLocaleString()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredMetrics.length === 0 && (
            <div className="text-center py-8 text-gray-400">No events found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BehaviorMetricsDashboard;